import {useEffect, useState, useContext} from "preact/hooks";
import {useLocation} from "../hooks/useLocation.js";
import messages from "../messages/messages.js";
import API_URL from "../includes/api.js";
import {handleRandomFavorites} from "../includes/requests.js";
import {NotificationContext} from '../components/NotificationContext.jsx';
import {useAuth} from '../components/AuthProvider.jsx';
import {LoadingContext} from '../components/LoadingContext.jsx';
import {CircleSpinner, RingSpinner} from './Spinner.jsx';
import {
    Bars3Icon as HamburgerIcon,
    XMarkIcon,
    GlobeAltIcon,
    HeartIcon,
    HomeIcon,
    LockClosedIcon,
    MegaphoneIcon,
    MusicalNoteIcon,
    PaintBrushIcon,
    PlayPauseIcon,
    LightBulbIcon,
    CreditCardIcon,
    TrophyIcon
} from "@heroicons/react/24/solid";

export function AccountSidebar() {

    const location = useLocation();

    const {addNotification} = useContext(NotificationContext);
    const {setLoading} = useContext(LoadingContext);
    const {getToken, user, authLoading} = useAuth();

    const [loadingDiscord, setLoadingDiscord] = useState(false);
    const [loadingYoutube, setLoadingYoutube] = useState(false);
    const [discord, setDiscord] = useState(null);
    const [discordID, setDiscordID] = useState(null);
    const [youtube, setYoutube] = useState(null);
    const [youtubeID, setYoutubeID] = useState(null);
    const [connectionLoading, setconnectionLoading] = useState(true);
    const [sidebarOpen, setSidebarOpen] = useState(false);

    useEffect(() => {
        if (authLoading) return;

        if (!user) {
            location.route("/login");
            return;
        }

        getToken().then(token => {
            fetch(API_URL + "/account/getinfo?firebase_auth_token=" + token)
                .then(response => response.json()).then(data => {
                if (data.code === 200) {
                    setDiscord(data.data.discord);
                    setDiscordID(data.data.discord_id);
                    setYoutubeID(data.data.channel_id);
                    setYoutube(data.data.channel_name);
                    setconnectionLoading(false);
                }
            });
        });
    }, [authLoading, user])

    const handleLinkDiscord = async () => {
        const clientId = import.meta.env.VITE_DISCORD_CLIENT_ID;
        const redirectUri = encodeURIComponent(API_URL + "/account/link_discord");

        setLoadingDiscord(true);
        const token = await getToken();
        const stateData = {
            returnUrl: window.location.origin + window.location.pathname,
            firebase_auth_token: token,
        };
        const state = encodeURIComponent(btoa(JSON.stringify(stateData)));

        const url = `https://discord.com/api/oauth2/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=identify&state=${state}`;
        window.location.href = url;
    };

    const disconnectDiscord = async () => {
        setLoadingDiscord(true);
        const token = await getToken();
        fetch(API_URL + "/account/disconnect_discord?firebase_auth_token=" + token)
            .then(response => response.json()).then(data => {
            if (data.code !== 200) {
                addNotification(data.message, 'error');
            } else {
                setDiscord(null);
                setDiscordID(null);
                setLoadingDiscord(false);
            }
        });
    }

    const handleLinkYoutube = async () => {
        setLoadingYoutube(true);
        const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
        const redirectUri = encodeURIComponent(
            API_URL + "/account/link_youtube"
        );

        const token = await getToken();
        const stateData = {
            returnUrl: window.location.origin + window.location.pathname,
            firebase_auth_token: token,
        };
        const state = encodeURIComponent(btoa(JSON.stringify(stateData)));

        const scope = encodeURIComponent("https://www.googleapis.com/auth/youtube.readonly");

        const url = `https://accounts.google.com/o/oauth2/v2/auth?` +
            `client_id=${clientId}` +
            `&redirect_uri=${redirectUri}` +
            `&response_type=code` +
            `&scope=${scope}` +
            `&access_type=offline` +
            `&state=${state}`;
        window.location.href = url;
    };

    const disconnectYoutube = async () => {
        setLoadingYoutube(true);
        const token = await getToken();
        fetch(API_URL + "/account/disconnect_youtube?firebase_auth_token=" + token)
            .then(response => response.json()).then(data => {
            if (data.code !== 200) {
                addNotification(data.message, 'error');
            } else {
                setYoutube(null);
                setYoutubeID(null);
                setLoadingYoutube(false);
            }
        });
    }

    function SidebarLink() {
        return (
            <>
                <div className="md:border border-border/60 bg-zinc-800 rounded-lg md:p-6 mb-4">

                    <h1 className="text-xl">My account</h1>

                    <div className="mb-4 flex flex-col gap-4">
                        <a href="/account"
                           className="text-white flex items-center gap-1">
                            <HomeIcon className="fill-white pe-1 w-6 h-6"/>
                            <span className="leading-3">Dashboard</span>
                        </a>
                        <a href="/account/change-password"
                           className="text-white flex items-center gap-1">
                            <LockClosedIcon className="fill-white pe-1 w-6 h-6"/>
                            <span className="leading-3">Change Password</span>
                        </a>
                        <a href="/account/manage-subscription"
                           className="text-white flex items-center gap-1">
                            <CreditCardIcon className="fill-white pe-1 w-6 h-6"/>
                            <span className="leading-3">Manage subscription</span>
                        </a>
                        <a href="/account/playlists"
                           className="text-white flex items-center gap-1">
                            <PlayPauseIcon className="fill-white pe-1 w-6 h-6"/>
                            <span className="leading-3">My Playlists <span
                                className="text-sm text-red-400">(BETA)</span></span>
                        </a>
                        <a href="/account/event-submissions"
                           className="text-white flex items-center gap-1">
                            <TrophyIcon className="fill-white pe-1 w-6 h-6"/>
                            <span className="leading-3">Event Submissions</span>
                        </a>
                        <button type="button" onClick={() => handleRandomFavorites(getToken, location, setLoading, addNotification)}
                             className="text-white flex items-center gap-1 cursor-pointer">
                            <HeartIcon className="fill-white pe-1 w-6 h-6" aria-hidden="true"/>
                            <span className="leading-3">Play Favorites</span>
                        </button>
                    </div>

                    <h3 className="text-xl"><b className="text-red-400">Subscriber</b> benefits</h3>
                    <div className="flex flex-col gap-4">
                        {messages["show_maker"] === "true" && (
                            <a href="/account/maker"
                               className="text-white flex items-center gap-1">
                                <MusicalNoteIcon className="fill-white pe-1 w-6 h-6"/>
                                <span className="leading-3">{messages["MAKER.NAME"]} <span className="text-sm text-red-400">(Free Trial)</span></span>
                            </a>
                        )}
                        <a href="/account/website"
                           className="text-white flex items-center gap-1">
                            <GlobeAltIcon className="fill-white pe-1 w-6 h-6"/>
                            <span className="leading-3">My Website</span>
                        </a>
                        <a href="/account/promotion"
                           className="text-white flex items-center gap-1">
                            <MegaphoneIcon className="fill-white pe-1 w-6 h-6"/>
                            <span className="leading-3">Channel Promotion</span>
                        </a>
                        <a href="/account/song-idea"
                           className="text-white flex items-center gap-1">
                            <LightBulbIcon className="fill-white pe-1 w-6 h-6"/>
                            <span className="leading-3">Song Idea Generator</span>
                        </a>
                        {messages['CHANNEL_ARTWORK_ENABLED'] === "true" && (
                            <a href="/account/artwork"
                               className="text-white flex items-center gap-1">
                                <PaintBrushIcon className="fill-white pe-1 w-6 h-6"/>
                                <span className="leading-3">Channel Artwork</span>
                            </a>
                        )}
                    </div>
                </div>

                <div className="md:border border-border/60 bg-zinc-800 rounded-lg md:p-6 mb-4">
                    <h3 className="text-xl mb-4">Socials</h3>

                    <div className="flex gap-4 flex-wrap">
                        {connectionLoading ? (
                            <div className="flex items-center">
                                <CircleSpinner/>
                                <h3 className="text-lg m-0">Loading...</h3>
                            </div>
                        ) : (
                            [
                                {
                                    label: "Discord",
                                    connected: !!discordID,
                                    name: discord,
                                    onLink: handleLinkDiscord,
                                    onDisconnect: disconnectDiscord,
                                    loading: loadingDiscord,
                                    icon: <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"
                                               className="h-6 w-6 me-2 fill-white group-hover:fill-black">
                                        <path
                                            d="M524.5 133.8C524.3 133.5 524.1 133.2 523.7 133.1C485.6 115.6 445.3 103.1 404 96C403.6 95.9 403.2 96 402.9 96.1C402.6 96.2 402.3 96.5 402.1 96.9C396.6 106.8 391.6 117.1 387.2 127.5C342.6 120.7 297.3 120.7 252.8 127.5C248.3 117 243.3 106.8 237.7 96.9C237.5 96.6 237.2 96.3 236.9 96.1C236.6 95.9 236.2 95.9 235.8 95.9C194.5 103 154.2 115.5 116.1 133C115.8 133.1 115.5 133.4 115.3 133.7C39.1 247.5 18.2 358.6 28.4 468.2C28.4 468.5 28.5 468.7 28.6 469C28.7 469.3 28.9 469.4 29.1 469.6C73.5 502.5 123.1 527.6 175.9 543.8C176.3 543.9 176.7 543.9 177 543.8C177.3 543.7 177.7 543.4 177.9 543.1C189.2 527.7 199.3 511.3 207.9 494.3C208 494.1 208.1 493.8 208.1 493.5C208.1 493.2 208.1 493 208 492.7C207.9 492.4 207.8 492.2 207.6 492.1C207.4 492 207.2 491.8 206.9 491.7C191.1 485.6 175.7 478.3 161 469.8C160.7 469.6 160.5 469.4 160.3 469.2C160.1 469 160 468.6 160 468.3C160 468 160 467.7 160.2 467.4C160.4 467.1 160.5 466.9 160.8 466.7C163.9 464.4 167 462 169.9 459.6C170.2 459.4 170.5 459.2 170.8 459.2C171.1 459.2 171.5 459.2 171.8 459.3C268 503.2 372.2 503.2 467.3 459.3C467.6 459.2 468 459.1 468.3 459.1C468.6 459.1 469 459.3 469.2 459.5C472.1 461.9 475.2 464.4 478.3 466.7C478.5 466.9 478.7 467.1 478.9 467.4C479.1 467.7 479.1 468 479.1 468.3C479.1 468.6 479 468.9 478.8 469.2C478.6 469.5 478.4 469.7 478.2 469.8C463.5 478.4 448.2 485.7 432.3 491.6C432.1 491.7 431.8 491.8 431.6 492C431.4 492.2 431.3 492.4 431.2 492.7C431.1 493 431.1 493.2 431.1 493.5C431.1 493.8 431.2 494 431.3 494.3C440.1 511.3 450.1 527.6 461.3 543.1C461.5 543.4 461.9 543.7 462.2 543.8C462.5 543.9 463 543.9 463.3 543.8C516.2 527.6 565.9 502.5 610.4 469.6C610.6 469.4 610.8 469.2 610.9 469C611 468.8 611.1 468.5 611.1 468.2C623.4 341.4 590.6 231.3 524.2 133.7zM222.5 401.5C193.5 401.5 169.7 374.9 169.7 342.3C169.7 309.7 193.1 283.1 222.5 283.1C252.2 283.1 275.8 309.9 275.3 342.3C275.3 375 251.9 401.5 222.5 401.5zM417.9 401.5C388.9 401.5 365.1 374.9 365.1 342.3C365.1 309.7 388.5 283.1 417.9 283.1C447.6 283.1 471.2 309.9 470.7 342.3C470.7 375 447.5 401.5 417.9 401.5z"/>
                                    </svg>
                                },
                                {
                                    label: "YouTube",
                                    connected: !!youtubeID,
                                    onLink: handleLinkYoutube,
                                    onDisconnect: disconnectYoutube,
                                    loading: loadingYoutube,
                                    icon: <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"
                                               className="h-6 w-6 me-2 fill-white group-hover:fill-black">
                                        <path
                                            d="M581.7 188.1C575.5 164.4 556.9 145.8 533.4 139.5C490.9 128 320.1 128 320.1 128C320.1 128 149.3 128 106.7 139.5C83.2 145.8 64.7 164.4 58.4 188.1C47 231 47 320.4 47 320.4C47 320.4 47 409.8 58.4 452.7C64.7 476.3 83.2 494.2 106.7 500.5C149.3 512 320.1 512 320.1 512C320.1 512 490.9 512 533.5 500.5C557 494.2 575.5 476.3 581.8 452.7C593.2 409.8 593.2 320.4 593.2 320.4C593.2 320.4 593.2 231 581.8 188.1zM264.2 401.6L264.2 239.2L406.9 320.4L264.2 401.6z"/>
                                    </svg>
                                }
                            ].map((conn, idx) => (
                                <button key={conn.label}
                                        onClick={() => conn.connected
                                            ? window.confirm(`Are you sure you want to disconnect your ${conn.label} account?`) && conn.onDisconnect()
                                            : conn.onLink()
                                        }
                                        className="group px-4 flex items-center cursor-pointer text-white border border-white hover:bg-white hover:text-black font-medium rounded-lg text-sm py-2 text-center"
                                >
                                    {conn.icon}
                                    {conn.connected ? "Disconnect" : "Connect"}
                                    {conn.loading && <RingSpinner/>}
                                </button>
                            ))
                        )}
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <button
                className="md:hidden bg-zinc-800 p-2 mb-3 rounded"
                aria-label="Open account menu"
                aria-expanded={sidebarOpen}
                onClick={() => setSidebarOpen(true)}
            >
                <HamburgerIcon className="w-6 h-6 text-white" aria-hidden="true"/>
            </button>

            <div
                className={`fixed inset-0 z-40 bg-black bg-opacity-50 transition-opacity ${sidebarOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"} md:hidden`}
                onClick={() => setSidebarOpen(false)}
            >
                <div
                    className={`fixed left-0 top-0 h-full w-3/4 bg-zinc-800 p-6 transition-transform ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Account menu"
                    onClick={e => e.stopPropagation()}
                >
                    <button className="absolute top-4 right-4" aria-label="Close account menu" onClick={() => setSidebarOpen(false)}>
                        <XMarkIcon className="w-6 h-6 text-white" aria-hidden="true"/>
                    </button>
                    <SidebarLink/>
                </div>
            </div>

            <div className="hidden md:block w-full md:w-1/4">
                <SidebarLink/>
            </div>
        </>
    );
}
